import { canPerformCapability, type CallerIdentity } from '@myplayer/rbac';
import {
  callUpdateWorkspaceFunction,
  type UpdateWorkspaceCallablePayload,
  type UpdateWorkspaceCallableResult,
} from './firebase.js';
import { type GenerateGs1QrPrintAssetsInput } from './qr-generator.js';
import {
  DEFAULT_SAFE_QR_STYLE,
  evaluateQrStyleRules,
  normalizeHexCode,
  type QrStyleConfig,
} from './qr-style-rules.js';
import { generateStyledQrAssets } from './qr-styled-generator.js';

export type QrCustomisationScope =
  | { readonly scope: 'workspace'; readonly workspaceId: string }
  | { readonly scope: 'brand'; readonly workspaceId: string; readonly brandName: string };

interface StoredQrCustomisations {
  workspaceStyles: Record<string, QrStyleConfig>;
  brandStyles: Record<string, Record<string, QrStyleConfig>>;
}

const inMemoryStore: StoredQrCustomisations = {
  workspaceStyles: {},
  brandStyles: {},
};

const LOCAL_STORAGE_KEY = 'mp_admin_qr_styles_v1';

export function normalizeBrandKey(brandName: string): string {
  return brandName.trim().toLowerCase().replace(/\s+/g, ' ');
}

function readPersistedStore(): StoredQrCustomisations {
  if (typeof window === 'undefined') return inMemoryStore;
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return inMemoryStore;
    const parsed = JSON.parse(raw) as Partial<StoredQrCustomisations>;
    return {
      workspaceStyles: { ...inMemoryStore.workspaceStyles, ...(parsed.workspaceStyles ?? {}) },
      brandStyles: { ...inMemoryStore.brandStyles, ...(parsed.brandStyles ?? {}) },
    };
  } catch {
    return inMemoryStore;
  }
}

function writePersistedStore(next: StoredQrCustomisations): void {
  inMemoryStore.workspaceStyles = { ...next.workspaceStyles };
  inMemoryStore.brandStyles = { ...next.brandStyles };
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage write errors in restricted environments
  }
}

export function resetQrStyleStoreForTests(): void {
  inMemoryStore.workspaceStyles = {};
  inMemoryStore.brandStyles = {};
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
}

export function sanitizeSavedQrStyle(style: QrStyleConfig): QrStyleConfig {
  return {
    fgHex: normalizeHexCode(style.fgHex),
    bgHex: normalizeHexCode(style.bgHex),
    dotShape: style.dotShape,
    showLogo: style.showLogo,
    logoScalePercent: style.logoScalePercent,
    logoLabel: (style.logoLabel.trim() || 'MP').slice(0, 4),
    errorCorrectionLevel: style.errorCorrectionLevel ?? 'H',
    quietZoneModules: style.quietZoneModules ?? 4,
  };
}

export type SaveBrandQrStyleCallableFn = (
  payload: UpdateWorkspaceCallablePayload,
) => Promise<
  UpdateWorkspaceCallableResult | { readonly ok?: boolean; readonly workspaceId: string }
>;

export interface SaveStyledQrDesignParams extends GenerateGs1QrPrintAssetsInput {
  readonly style: QrStyleConfig;
  readonly target: QrCustomisationScope;
  readonly caller?: CallerIdentity;
  readonly orgId?: string;
  readonly onUpdateWorkspaceCallable?: SaveBrandQrStyleCallableFn;
}

export type SaveStyledQrDesignOutcome =
  | {
      readonly ok: true;
      readonly status: 'valid';
      readonly decodedUrl: string;
      readonly savedStyle: QrStyleConfig;
      readonly callablePayload: UpdateWorkspaceCallablePayload;
    }
  | {
      readonly ok: false;
      readonly status: string;
      readonly decodedUrl: string | null;
    };

/**
 * Validates that a styled QR design passes all styled rules AND passes the `jsQR` decode test
 * returning the exact uncompressed GS1 Digital Link URL before saving it via the `updateWorkspace`
 * callable (never a direct client write to Firestore).
 */
export async function saveStyledQrDesign(
  params: SaveStyledQrDesignParams,
): Promise<SaveStyledQrDesignOutcome> {
  const { style, target, caller, orgId, onUpdateWorkspaceCallable, ...qrInput } = params;
  const generated = generateStyledQrAssets({
    ...qrInput,
    style,
  });

  const decodeMatchesExactUrl = generated.decodePassed && generated.decodedUrl === generated.url;
  const ruleEvaluation = evaluateQrStyleRules(
    {
      ...style,
      errorCorrectionLevel: style.errorCorrectionLevel ?? qrInput.errorCorrectionLevel ?? 'H',
    },
    { decodePassed: decodeMatchesExactUrl },
  );

  if (!ruleEvaluation.valid) {
    return {
      ok: false,
      status: ruleEvaluation.status,
      decodedUrl: generated.decodedUrl,
    };
  }

  if (!decodeMatchesExactUrl || generated.decodedUrl === null) {
    return {
      ok: false,
      status:
        'Styled QR design could not be saved because the decode test did not return the exact GS1 Digital Link URL.',
      decodedUrl: generated.decodedUrl,
    };
  }

  const effectiveOrgId =
    orgId && orgId.trim().length > 0 ? orgId.trim() : (caller?.orgId ?? 'org_default');
  if (
    caller !== undefined &&
    !canPerformCapability(caller, 'workspace:update', effectiveOrgId, target.workspaceId)
  ) {
    return {
      ok: false,
      status: 'You do not have permission to save brand QR styles for this workspace.',
      decodedUrl: generated.decodedUrl,
    };
  }

  const savedStyle = sanitizeSavedQrStyle(style);
  const bKey = target.scope === 'brand' ? normalizeBrandKey(target.brandName) : '';
  const callablePayload: UpdateWorkspaceCallablePayload = {
    orgId: effectiveOrgId,
    workspaceId: target.workspaceId,
    ...(target.scope === 'brand' && bKey.length > 0
      ? { brandQrStyles: { [bKey]: savedStyle } }
      : { qrStyle: savedStyle }),
  };

  const callableFn = onUpdateWorkspaceCallable ?? callUpdateWorkspaceFunction;
  await callableFn(callablePayload);

  const store = readPersistedStore();
  if (target.scope === 'workspace') {
    store.workspaceStyles[target.workspaceId] = savedStyle;
  } else if (bKey.length > 0) {
    const wsBrands = { ...(store.brandStyles[target.workspaceId] ?? {}) };
    wsBrands[bKey] = savedStyle;
    store.brandStyles[target.workspaceId] = wsBrands;
  } else {
    store.workspaceStyles[target.workspaceId] = savedStyle;
  }
  writePersistedStore(store);

  return {
    ok: true,
    status: 'valid',
    decodedUrl: generated.decodedUrl,
    savedStyle,
    callablePayload,
  };
}

export function getSavedWorkspaceQrStyle(workspaceId: string): QrStyleConfig | null {
  const store = readPersistedStore();
  return store.workspaceStyles[workspaceId] ?? null;
}

export function getSavedBrandQrStyle(workspaceId: string, brandName: string): QrStyleConfig | null {
  const bKey = normalizeBrandKey(brandName);
  if (bKey.length === 0) return null;
  const store = readPersistedStore();
  return store.brandStyles[workspaceId]?.[bKey] ?? null;
}

export function resolveEffectiveQrStyle(params: {
  readonly workspaceId: string;
  readonly brandName?: string | undefined;
  readonly fallbackStyle?: QrStyleConfig | undefined;
}): QrStyleConfig {
  if (params.brandName !== undefined && params.brandName.trim().length > 0) {
    const brandStyle = getSavedBrandQrStyle(params.workspaceId, params.brandName);
    if (brandStyle !== null) return brandStyle;
  }
  const workspaceStyle = getSavedWorkspaceQrStyle(params.workspaceId);
  if (workspaceStyle !== null) return workspaceStyle;
  return params.fallbackStyle ?? DEFAULT_SAFE_QR_STYLE;
}
